import { useEffect, useState } from 'react'
import AdminDialog from '@/components/AdminDialog'
import { errorMessage } from '@/api/client'
import { productionCommissionsApi } from '@/api/productionCommissions'
import ProductionCommissionApplications from './ProductionCommissionApplications'
import ProductionCommissionWorkflow from './ProductionCommissionWorkflow'
import { recruitmentLabels, type ProductionCommission } from './productionCommissionsModel'
import { useVisibleRefresh } from '@/hooks/useVisibleRefresh'

type DetailTab = 'applications' | 'script' | 'episodes'

export default function ProductionCommissionDetail({ offer, initialTab = 'applications', onClose, onChanged, preview = false, readOnly = false }: {
  offer: ProductionCommission
  initialTab?: DetailTab
  onClose: () => void
  onChanged: (commission?: ProductionCommission) => void
  preview?: boolean
  readOnly?: boolean
}) {
  const [commission, setCommission] = useState(offer)
  const [tab, setTab] = useState<DetailTab>(initialTab)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function refresh() {
    if (preview) return
    const [next, applications] = await Promise.all([productionCommissionsApi.get(offer.id), productionCommissionsApi.applications(offer.id)])
    setCommission({ ...next, applications: applications.items })
    onChanged(next)
  }

  useVisibleRefresh(() => {
    if (!busy) void refresh().catch(cause => setError(errorMessage(cause)))
  }, !preview)

  useEffect(() => {
    void refresh().catch(cause => setError(errorMessage(cause)))
    // The detail dialog is bound to one commission for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.id])

  async function review(ids: string[], status: 'approved' | 'rejected', reason: string, scores: Record<string, number>) {
    setBusy(true)
    try {
      if (preview) {
        const next = { ...commission, applications: commission.applications.map(item => ids.includes(item.id) ? { ...item, status, score: status === 'approved' ? scores[item.id] : undefined, rejectionReason: status === 'rejected' ? reason : undefined } : item) }
        setCommission(next)
        onChanged(next)
      } else {
        await productionCommissionsApi.review(offer.id, ids, status, reason, scores)
        await refresh()
        onChanged()
      }
    } finally { setBusy(false) }
  }

  async function selectWinner(applicationId: string) {
    setBusy(true)
    try {
      if (preview) {
        const next: ProductionCommission = { ...commission, winnerApplicationId: applicationId, status: 'awaiting_contract', applications: commission.applications.map(item => ({ ...item, isWinner: item.id === applicationId })) }
        setCommission(next)
        onChanged(next)
      } else {
        await productionCommissionsApi.selectWinner(offer.id, applicationId)
        await refresh()
        onChanged()
      }
      setTab('script')
    } finally { setBusy(false) }
  }

  async function beginReview() {
    setBusy(true)
    setError('')
    try {
      if (preview) {
        const next: ProductionCommission = { ...commission, status: 'reviewing' }
        setCommission(next)
        onChanged(next)
      } else {
        const next = await productionCommissionsApi.beginReview(offer.id, commission.revision ?? 0)
        setCommission(current => ({ ...current, ...next }))
        onChanged(next)
      }
    } catch (cause) { setError(errorMessage(cause)) }
    finally { setBusy(false) }
  }

  const tabs: Array<[DetailTab, string]> = [['applications', '试稿审核'], ['script', '剧本解锁'], ['episodes', '分集交付']]
  return <AdminDialog title={`商单详情 · ${commission.title}`} onClose={onClose} busy={busy} extraWide>
    <div role="tablist" aria-label="商单详情" className="mb-4 grid grid-cols-3 overflow-hidden rounded-lg border border-slate-200">
      {tabs.map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={tab === key} disabled={busy} onClick={() => setTab(key)} className={`border-r border-slate-200 px-4 py-3 text-sm last:border-0 disabled:opacity-50 ${tab === key ? 'bg-brand-50 font-semibold text-brand-700' : 'bg-slate-50 text-slate-600 hover:bg-white'}`}>{label}</button>)}
    </div>
    <p className="mb-4 text-sm text-slate-500">{commission.title} · {recruitmentLabels[commission.status]} · {commission.applicationCount ?? commission.applications.length} 份投稿</p>
    {tab === 'applications' && !readOnly && !commission.winnerApplicationId && ['recruiting', 'paused', 'ended'].includes(commission.status) && (commission.applicationCount ?? commission.applications.length) > 0 && <button type="button" disabled={busy} onClick={() => { if (window.confirm('进入评选后将停止接收新申请，确认继续吗？')) void beginReview() }} className="mb-4 rounded-lg border border-brand-600 px-3 py-2 text-sm text-brand-700 disabled:opacity-50">结束招募并进入评选</button>}
    {error && <p role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    {tab === 'applications' ? <ProductionCommissionApplications offer={commission} onClose={onClose} onReview={review} onWinner={selectWinner} readOnly={readOnly} embedded /> :
      <ProductionCommissionWorkflow key={`${commission.id}-${tab}`} offer={commission} onClose={onClose} readOnly={readOnly || preview} section={tab} embedded preview={preview} onBusyChange={setBusy} />}
  </AdminDialog>
}
